import { Button, Checkbox, Group, Loader, Modal, Radio, Stack, Text, TextInput } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  cloneRobot,
  CloneRobotPreviewResponse,
  DatasetCloneMode,
  getCloneRobotPreview,
} from '@mygpt/app/apis/services/robots';

interface Props {
  opened: boolean;
  onClose: () => void;
  aiId: string;
  /** Called after the clone request returns. The parent should refresh the bot list. */
  onCloned?: (immediateDone: boolean, taskId: string, newRobotId: string) => void;
}

const DEFAULT_MODE: DatasetCloneMode = 'share';

export default function CloneBotModal({ opened, onClose, aiId, onCloned }: Props) {
  const { t } = useTranslation();
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [preview, setPreview] = useState<CloneRobotPreviewResponse | null>(null);
  const [name, setName] = useState('');
  const [datasetModes, setDatasetModes] = useState<Record<string, DatasetCloneMode>>({});
  const [includeChatHistory, setIncludeChatHistory] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!opened) return;
    setLoadingPreview(true);
    setPreview(null);
    setIncludeChatHistory(false);
    getCloneRobotPreview(aiId)
      .then((res) => {
        setPreview(res);
        setName(res.suggested_new_name);
        setDatasetModes(
          res.datasets.reduce<Record<string, DatasetCloneMode>>((acc, d) => {
            acc[d.id] = DEFAULT_MODE;
            return acc;
          }, {})
        );
      })
      .catch((err) => {
        showNotification({
          message: err?.message || t('clone_bot.preview_failed'),
          color: 'red',
        });
        onClose();
      })
      .finally(() => setLoadingPreview(false));
  }, [aiId, opened]);

  const handleSubmit = async () => {
    if (!preview) return;
    setSubmitting(true);
    try {
      const res = await cloneRobot({
        aiId,
        name: name.trim() || undefined,
        include_chat_history: includeChatHistory,
        datasets: Object.entries(datasetModes).map(([dataset_id, mode]) => ({
          dataset_id,
          mode,
        })),
      });
      onCloned?.(res.immediate_done, res.task_id, res.new_robot_id);
      showNotification({
        message: res.immediate_done ? t('clone_bot.success') : t('clone_bot.submit_async'),
        color: 'green',
      });
      onClose();
    } catch (err: any) {
      showNotification({
        message: err?.message || t('clone_bot.failed'),
        color: 'red',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal opened={opened} onClose={onClose} title={t('clone_bot.title')} size="lg" centered>
      {loadingPreview || !preview ? (
        <Group justify="center" py="xl">
          <Loader />
        </Group>
      ) : (
        <Stack gap="md">
          <TextInput
            label={t('clone_bot.name_label')}
            value={name}
            onChange={(e) => setName(e.currentTarget.value)}
            maxLength={100}
          />

          {preview.datasets.length > 0 && (
            <Stack gap="xs">
              <Text fw={500}>{t('clone_bot.section_datasets')}</Text>
              {preview.datasets.map((d) => {
                const parts: string[] = [];
                const websitesTotal = (d.websites_count || 0) + (d.integrations_count || 0);
                if (d.documents_count > 0) parts.push(`${t('clone_bot.resource.documents')}: ${d.documents_count}`);
                if (websitesTotal > 0) parts.push(`${t('clone_bot.resource.websites')}: ${websitesTotal}`);
                if (d.faqs_count > 0) parts.push(`${t('clone_bot.resource.faqs')}: ${d.faqs_count}`);
                return (
                  <Stack key={d.id} gap={4}>
                    <Text size="sm">
                      {d.name}
                      {parts.length > 0 && (
                        <Text span c="gray.5" size="xs">
                          {' '}
                          ({parts.join(' · ')})
                        </Text>
                      )}
                    </Text>
                    <Radio.Group
                      value={datasetModes[d.id] || DEFAULT_MODE}
                      onChange={(v) => setDatasetModes((prev) => ({ ...prev, [d.id]: v as DatasetCloneMode }))}
                    >
                      <Group gap="lg">
                        <Radio value="share" label={t('clone_bot.dataset_mode.share')} />
                        <Radio value="clone" label={t('clone_bot.dataset_mode.clone')} />
                        <Radio value="skip" label={t('clone_bot.dataset_mode.skip')} />
                      </Group>
                    </Radio.Group>
                  </Stack>
                );
              })}
            </Stack>
          )}

          {preview.chat_history_summary.sessions_count > 0 && (
            <Checkbox
              label={t('clone_bot.include_chat_history', {
                sessions: preview.chat_history_summary.sessions_count,
                questions: preview.chat_history_summary.questions_count,
              })}
              checked={includeChatHistory}
              onChange={(e) => setIncludeChatHistory(e.currentTarget.checked)}
            />
          )}

          <Group justify="flex-end" mt="md">
            <Button variant="outline" onClick={onClose} disabled={submitting}>
              {t('alert_cancel')}
            </Button>
            <Button onClick={handleSubmit} loading={submitting}>
              {t('clone_bot.submit')}
            </Button>
          </Group>
        </Stack>
      )}
    </Modal>
  );
}
